import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useTeamMembers } from "@/hooks/useTeam";
interface RoutingCriteria {
  performance_weight: number;
  workload_weight: number;
  region_match_weight: number;
  specialization_weight: number;
}

interface AgentScore {
  user_id: string;
  name: string;
  email: string;
  score: number;
  current_leads: number;
  win_rate: number;
  avg_response_time: number;
}

const DEFAULT_CRITERIA: RoutingCriteria = {
  performance_weight: 0.4,
  workload_weight: 0.3,
  region_match_weight: 0.2,
  specialization_weight: 0.1,
};

export function useLeadAutoRouting() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data: teamMembers, isLoading: isLoadingTeam } = useTeamMembers();

  const hasTeamMembers = teamMembers && teamMembers.length > 0;

  // Calculate agent scores for routing
  const calculateAgentScores = async (leadId?: string): Promise<AgentScore[]> => {
    if (!teamMembers || teamMembers.length === 0) return [];

    const scores: AgentScore[] = [];

    for (const member of teamMembers) {
      // Get current workload (assigned leads)
      const { data: assignedLeads } = await supabase
        .from('leads')
        .select('id')
        .eq('assigned_to_user_id', member.id)
        .in('status', ['new', 'attempted_contact', 'engaged']);

      // Get performance metrics (won deals)
      const { data: deals } = await supabase
        .from('deals')
        .select('status')
        .eq('owner_user_id', member.id);

      const totalDeals = deals?.length || 0;
      const wonDeals = deals?.filter(d => d.status === 'won').length || 0;
      const winRate = totalDeals > 0 ? (wonDeals / totalDeals) * 100 : 50;

      // Calculate composite score
      const currentLeads = assignedLeads?.length || 0;
      const workloadScore = Math.max(0, 100 - (currentLeads * 10)); // Penalize high workload
      const performanceScore = winRate;

      const compositeScore = 
        (performanceScore * DEFAULT_CRITERIA.performance_weight) +
        (workloadScore * DEFAULT_CRITERIA.workload_weight) +
        (50 * DEFAULT_CRITERIA.region_match_weight) + // Placeholder for region matching
        (50 * DEFAULT_CRITERIA.specialization_weight); // Placeholder for specialization

      scores.push({
        user_id: member.id,
        name: `${member.first_name || ''} ${member.last_name || ''}`.trim() || member.email,
        email: member.email,
        score: Math.round(compositeScore),
        current_leads: currentLeads,
        win_rate: Math.round(winRate),
        avg_response_time: Math.random() * 24, // Placeholder
      });
    }

    return scores.sort((a, b) => b.score - a.score);
  };

  // Auto-route a single lead
  const autoRouteLead = useMutation({
    mutationFn: async (leadId: string) => {
      const scores = await calculateAgentScores(leadId);
      
      if (scores.length === 0) {
        throw new Error('No available agents for routing');
      }

      const bestAgent = scores[0];

      const { error } = await supabase
        .from('leads')
        .update({ assigned_to_user_id: bestAgent.user_id })
        .eq('id', leadId);

      if (error) throw error;

      return { leadId, assignedTo: bestAgent };
    },
    onSuccess: (data) => {
      toast({
        title: "Lead Routed",
        description: `Lead assigned to ${data.assignedTo.name} (score: ${data.assignedTo.score})`,
      });
      queryClient.invalidateQueries({ queryKey: ['leads'] });
    },
    onError: (error) => {
      toast({
        title: "Routing Failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Auto-route multiple unassigned leads
  const autoRouteUnassignedLeads = useMutation({
    mutationFn: async () => {
      const { data: unassignedLeads, error: fetchError } = await supabase
        .from('leads')
        .select('id')
        .is('assigned_to_user_id', null)
        .in('status', ['new', 'attempted_contact']);

      if (fetchError) throw fetchError;
      if (!unassignedLeads || unassignedLeads.length === 0) {
        return { routed: 0 };
      }

      const scores = await calculateAgentScores();
      if (scores.length === 0) {
        throw new Error('No available agents for routing');
      }

      let routedCount = 0;
      let agentIndex = 0;

      // Round-robin with score weighting
      for (const lead of unassignedLeads) {
        const agent = scores[agentIndex % scores.length];
        
        const { error } = await supabase
          .from('leads')
          .update({ assigned_to_user_id: agent.user_id })
          .eq('id', lead.id);

        if (!error) {
          routedCount++;
          agentIndex++;
        }
      }

      return { routed: routedCount };
    },
    onSuccess: (data) => {
      toast({
        title: "Bulk Routing Complete",
        description: `${data.routed} leads have been auto-assigned to agents`,
      });
      queryClient.invalidateQueries({ queryKey: ['leads'] });
    },
    onError: (error) => {
      toast({
        title: "Bulk Routing Failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  return {
    calculateAgentScores,
    autoRouteLead,
    autoRouteUnassignedLeads,
    hasTeamMembers,
    isLoadingTeam,
  };
}
